import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import type { RootState } from '../../../store/store';
import Permission from '../../../common/permissions';
import { canSeeCustomersArea } from '../../../common/customersAreaAccess';
import { makeHasPermissionSelector, tenantSelector } from '../../../selector/authenticationSelector';
import PageHeader from '../../../components/PageHeader';
import PageHeadline from '../../../components/PageHeadline';
import SegmentedTabs from '../../../components/SegmentedTabs';
import { isRoomFamily, spaceOf } from '../../../utils/useCases';
import { useGetTenantWindowsQuery } from '../../../api/organization/organizationApiSlice';
import BookingsPage from './BookingsPage';
import AllocationsTab from './allocations/AllocationsTab';
import { useSeriesSeason } from '../../organizations/allocationWindows/useSeriesSeason';

type BookingsView = 'bookings' | 'allocations';
const VIEW_PARAM = 'view';

/**
 * The Bookings menu: the bookings, and beside them the allocations tab
 * (ADR-0027, PO 2026-10-03: "their own tab next to bookings under the
 * Bookings menu"). The tab is offered where allocations can exist: an
 * operational tenant (the window tables live there only) with a room use
 * case, to a role that may read organisations, once the tenant has a window
 * or (for a use case with `allocationWindows`) has set its series season. Everyone else sees the bookings page exactly
 * as before.
 *
 * The view is in the URL (`?view=allocations`), so a link or refresh lands on it.
 */
const BookingsArea: React.FC = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const tenant = useSelector(tenantSelector);
    const useCases = useSelector((state: RootState) => state.authentication.config?.services) ?? [];
    const canReadOrganizations = useSelector(makeHasPermissionSelector(Permission.readCustomers()));
    const mayHaveAllocations = canSeeCustomersArea(tenant)
        && canReadOrganizations
        && useCases.some((useCase: string) => isRoomFamily(useCase));
    const { data: windows } = useGetTenantWindowsQuery(undefined, { skip: !mayHaveAllocations });
    // A set series season switches it on too, for a use case that offers
    // allocations (`allocationWindows`: halls, PO 2026-10-25): a tenant that has
    // not entered a window yet must reach the plan import (W2-3) to enter its
    // first ones. Venues and apartments see the tab only once they have a window.
    const seriesSeason = useSeriesSeason();
    const offersBeforeFirstWindow = seriesSeason !== null
        && useCases.some((useCase: string) => spaceOf(useCase)?.allocationWindows === true);
    const offersAllocations = mayHaveAllocations && ((windows?.items.length ?? 0) > 0 || offersBeforeFirstWindow);

    const view: BookingsView = offersAllocations && searchParams.get(VIEW_PARAM) === 'allocations'
        ? 'allocations'
        : 'bookings';
    const changeView = (next: BookingsView) => {
        const params = new URLSearchParams(searchParams);
        if (next === 'allocations') params.set(VIEW_PARAM, next);
        else params.delete(VIEW_PARAM);
        setSearchParams(params);
    };

    if (!offersAllocations) return <BookingsPage />;

    const tabs = (
        <SegmentedTabs<BookingsView>
            className={'mb-4'}
            items={[
                { key: 'bookings', label: t('allocation_windows.tab.bookings') },
                { key: 'allocations', label: t('allocation_windows.tab.allocations') },
            ]}
            value={view}
            onChange={changeView}
        />
    );

    if (view === 'bookings') return <BookingsPage viewTabs={tabs} />;

    return (
        <div className={'px-0 sm:px-4 py-6'}>
            <PageHeader>
                <PageHeadline>{t('allocation_windows.title')}</PageHeadline>
            </PageHeader>
            {tabs}
            <AllocationsTab />
        </div>
    );
};

export default BookingsArea;
